import { useQuery } from "@tanstack/react-query";
import type { Id } from "@amber/contracts";
import type { FirstPlan, SuggestionRelationship } from "@amber/profiles/contracts";
import { useCsrf } from "@amber/ui";
import type { PlanPreview } from "../../contracts/index";
import { planPreviewQuery } from "../queries";

function estimateValue(known: number, data: PlanPreview | undefined, format: (data: PlanPreview) => string): string {
  if (known === 0) return "—";
  return data ? format(data) : "…";
}

export function Estimate({
  plan,
  selected,
}: {
  plan: FirstPlan;
  selected: readonly { source_id: Id<"src"> | null; relationship: SuggestionRelationship }[];
}) {
  const csrf = useCsrf();
  const known = selected.flatMap((s) =>
    s.source_id
      ? [
          {
            source_id: s.source_id,
            cadence_tier: s.relationship === "official_record" ? ("priority" as const) : ("standard" as const),
          },
        ]
      : [],
  );
  const preview = useQuery({
    ...planPreviewQuery({ sources: known, back_to: plan.back_to, owner_plan_id: null }, csrf()),
    enabled: known.length > 0,
  });
  const fresh = selected.length - known.length;
  const cell = (label: string, value: string) => (
    <div className="rounded-lg border border-divider px-2.5 py-2">
      <span className="block text-md text-muted">{label}</span>
      <b className="font-mono text-base font-semibold text-fg">{value}</b>
    </div>
  );
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        {cell("Sources", String(selected.length + plan.covered.length))}
        {cell(
          "Items per month",
          estimateValue(known.length, preview.data, (p) => `≈ ${String(Math.round(p.items_per_month))}`),
        )}
        {cell(
          "Cost per month",
          estimateValue(
            known.length,
            preview.data,
            (p) => `${p.cost_per_month.currency} ${Number(p.cost_per_month.amount_decimal).toFixed(2)}`,
          ),
        )}
      </div>
      <p className="m-0 mt-1.5 text-md text-muted">
        Download, archive and transcription; shared sources count once.
        {fresh > 0
          ? ` ${String(fresh)} new source${fresh === 1 ? " is" : "s are"} estimated after the first scans.`
          : ""}
      </p>
    </>
  );
}
